import { loadPdf } from './parse.js';
/**
 * Adapts a PDF (fetched via `inner.getFile`) into an `ImageManifest` +
 * canvas-rendered page blobs, so the **image engine** can read it unchanged.
 * Progress passes through to the wrapped source.
 */
export class PdfImageSource {
    #inner;
    #maxDim;
    #doc = null;
    #docPromise = null;
    constructor(inner, opts = {}) {
        this.#inner = inner;
        this.#maxDim = opts.maxDim ?? 2400;
    }
    /** Flattened outline for the shell's `reader:toc`. */
    async outline() {
        return (await this.#load()).outline;
    }
    /** Page count, for building search sections without re-touching pdf.js internals. */
    async pageCount() {
        return (await this.#load()).pageCount;
    }
    /** Plain text of one page (1-based), for in-book search. */
    async textContent(page) {
        return (await this.#load()).textContent(page);
    }
    /** Normalized text-run boxes for one page (1-based) — marquee → highlight. */
    async textRects(page) {
        return (await this.#load()).textRects(page);
    }
    async getManifest(bookId) {
        const doc = await this.#load(bookId);
        const sizes = await Promise.all(Array.from({ length: doc.pageCount }, (_, i) => doc.pageSize(i + 1)));
        const chapters = outlineToChapters(doc.outline);
        const manifest = {
            bookId,
            type: 'image',
            title: bookId,
            direction: 'ltr',
            pageCount: doc.pageCount,
            pages: sizes.map((s, index) => ({ index, width: s.width, height: s.height })),
            preferredLayout: 'paged-single',
            ...(chapters.length ? { chapters } : {}),
        };
        return manifest;
    }
    async getPage(bookId, index, opts) {
        if (opts?.signal?.aborted)
            throw new DOMException('Aborted', 'AbortError');
        const doc = await this.#load(bookId);
        return doc.renderToBlob(index + 1, { maxDim: this.#maxDim });
    }
    getFile(_bookId, _opts) {
        return Promise.reject(new Error('PdfImageSource: pages only'));
    }
    loadProgress(bookId) {
        return this.#inner.loadProgress(bookId);
    }
    saveProgress(bookId, p) {
        return this.#inner.saveProgress(bookId, p);
    }
    async dispose() {
        await this.#doc?.destroy();
        this.#doc = null;
        this.#docPromise = null;
    }
    #load(bookId) {
        if (this.#doc)
            return Promise.resolve(this.#doc);
        this.#docPromise ??= (async () => {
            const blob = await this.#inner.getFile(bookId ?? '');
            const doc = await loadPdf(new Uint8Array(await blob.arrayBuffer()));
            this.#doc = doc;
            return doc;
        })();
        return this.#docPromise;
    }
}
/** PDF page number is 1-based; TocEntry hrefs are `#page=N`. */
function outlineToChapters(outline) {
    const out = [];
    const walk = (entries) => {
        for (const e of entries) {
            const m = /#page=(\d+)/.exec(e.href);
            if (m)
                out.push({ id: e.href, label: e.label, startIndex: Number(m[1]) - 1 });
            walk(e.children);
        }
    };
    walk(outline);
    return out.sort((a, b) => a.startIndex - b.startIndex);
}
//# sourceMappingURL=pdf-source.js.map